import React from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { translations } from '../i18n/translations';
// ─────────────────────────────────────────────
// ✏️  AJOUTER / MODIFIER UN CLIENT ICI
// ─────────────────────────────────────────────
interface Client {
  en: {
    name: string;
    role: string;
    description: string;
  };
  fr: {
    name: string;
    role: string;
    description: string;
  };
  /** Ex: "2022 – 2024". Laisser vide pour masquer le badge. */
  period?: string;
  /** URL du logo. Si absent, les initiales sont affichées. */
  logoUrl?: string;
  /** Initiales affichées si pas de logoUrl */
  logoInitials: string;
  /** Couleur d'accentuation (hex) */
  accentColor: string;
  /** 'studio' = client réel | 'placeholder' = carte "votre studio" */
  type: 'studio' | 'placeholder';
}
const CLIENTS: Client[] = [
// ── Client 1 ──────────────────────────────
{
  en: {
    name: 'Masseka Game Studio',
    role: 'Gameplay Programmer — Apprenticeship',
    description: 'Worked as a gameplay programmer in an apprenticeship setting, contributing to game mechanics, systems design, and overall development pipeline within a professional studio environment.'
  },
  fr: {
    name: 'Masseka Game Studio',
    role: 'Programmeur Gameplay — Alternance',
    description: "Travaillé en tant que programmeur de jeu en alternance, contribuant aux mécaniques de jeu, à la conception de systèmes et au pipeline de développement global au sein d'un studio professionnel."
  },
  period: '2022 – 2024',
  logoUrl: 'https://res.cloudinary.com/dbl7f0vfr/image/upload/v1772027685/MassekaGameStudio_mymvvr.jpg',
  logoInitials: 'MGS',
  accentColor: '#00f5a0',
  type: 'studio'
},
// ── Carte "votre studio" (toujours en dernier) ──
{
  en: {
    name: 'Your Studio Here',
    role: 'Open for collaboration',
    description: "Currently available for new freelance projects and studio partnerships. Let's build something great together."
  },
  fr: {
    name: 'Votre Studio Ici',
    role: 'Ouvert à la collaboration',
    description: 'Actuellement disponible pour de nouveaux projets freelance et partenariats studio. Construisons quelque chose de grand ensemble.'
  },
  logoInitials: '?',
  accentColor: '#1a4fd6',
  type: 'placeholder'
}];
// ─────────────────────────────────────────────
export function ClientsSection() {
  const {
    language
  } = useLanguage();
  const t = translations[language].clients;
  return <section id="clients" className="py-24 relative overflow-hidden" data-id="element-44">
      {/* Background glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-64 bg-[#00c9a7]/5 rounded-full blur-3xl pointer-events-none" data-id="element-45"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-46">
        <div className="mb-16" data-id="element-47">
          <h2 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-48">{t.title}</h2>
          <div className="h-1 w-20 bg-gradient-to-r from-[#1a4fd6] to-[#00f5a0] rounded-full" data-id="element-49"></div>
          <p className="text-gray-400 mt-4 max-w-xl" data-id="element-50">{t.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl" data-id="element-51">
          {CLIENTS.map((client, index) => {
          const text = client[language];
          return <div key={index} className={`relative rounded-2xl p-8 border transition-all duration-500 ${client.type === 'placeholder' ? 'border-dashed border-white/10 bg-transparent group hover:border-[#1a4fd6]/40 hover:bg-[#1a4fd6]/5 cursor-default' : 'border-white/10 bg-white/3 hover:border-[#00f5a0]/30 hover:bg-white/5'}`} style={client.type === 'studio' ? {
            boxShadow: `0 0 40px ${client.accentColor}08`
          } : {}} data-id="element-52">
                <div className="flex items-start gap-5" data-id="element-53">
                  {/* Logo or initials */}
                  {client.logoUrl ? <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border border-white/10 bg-black/20" data-id="element-54">
                      <img src={client.logoUrl} alt={`${text.name} logo`} className="w-full h-full object-cover" data-id="element-55" />
                    </div> : <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 border border-white/10 transition-all duration-500 ${client.type === 'placeholder' ? 'group-hover:border-[#1a4fd6]/40' : ''}`} style={{
                background: `linear-gradient(135deg, ${client.accentColor}20, transparent)`,
                color: client.accentColor,
                fontFamily: 'Orbitron, sans-serif',
                fontSize: client.type === 'placeholder' ? '1.5rem' : '0.65rem'
              }} data-id="element-56">
                      {client.logoInitials}
                    </div>}

                  <div className="flex-1 min-w-0" data-id="element-57">
                    <div className="flex items-start justify-between gap-2 flex-wrap" data-id="element-58">
                      <h3 className={`text-lg font-bold leading-tight transition-colors duration-500 ${client.type === 'placeholder' ? 'text-white/30 group-hover:text-white/60' : 'text-white'}`} data-id="element-59">
                        {text.name}
                      </h3>
                      {client.period && <span className="text-xs px-2 py-1 rounded-full border flex-shrink-0" style={{
                    borderColor: `${client.accentColor}40`,
                    color: client.accentColor,
                    backgroundColor: `${client.accentColor}10`
                  }} data-id="element-60">
                          {client.period}
                        </span>}
                    </div>
                    <p className={`text-sm font-medium mt-1 mb-3 transition-colors duration-500 ${client.type === 'placeholder' ? 'text-white/20 group-hover:text-white/40' : ''}`} style={{
                  color: client.type === 'studio' ? client.accentColor : '#1a4fd6'
                }} data-id="element-61">
                      {text.role}
                    </p>
                    <p className={`text-sm leading-relaxed transition-colors duration-500 ${client.type === 'placeholder' ? 'text-gray-700 group-hover:text-gray-500' : 'text-gray-400'}`} data-id="element-62">
                      {text.description}
                    </p>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Full-width decorative code snippet */}
        <div className="mt-10 p-5 rounded-xl border border-[#00c9a7]/20 bg-[#00c9a7]/5 backdrop-blur-sm" data-id="element-63">
          <p className="font-mono text-[#00c9a7] text-sm leading-relaxed" data-id="element-64">
            <span className="text-gray-600" data-id="element-65"> // collaboration.log</span>
            <br data-id="element-66" />
            &gt; Connection.Establish(
            <span className="text-[#00f5a0]" data-id="element-67">"new_partner"</span>);
            <br data-id="element-68" />
            &gt; Trust.Build(quality, deadlines, communication);
            <br data-id="element-69" />
            &gt;{' '}
            <span className="text-white/60" data-id="element-70"> // Ready for next mission_</span>
          </p>
        </div>
      </div>
    </section>;
}